import { ChipsContainer } from 'app/ComponentLibrary/Filter/ChipsContainer';
import React, { createRef, useEffect, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { Collapse, Grid } from '@mui/material';
import { SearchPageIcons } from 'app/ComponentLibrary/Filter/SearchPageIcons';
import { makeStyles } from 'tss-react/mui';
import { selectFilters } from '@AMIEWEB/Common/Grid/GridStateManagement/GridState.selectors';
import { determineChipSelected, generateFieldChips } from '@AMIEWEB/AmWorkDesk/PlacementsDesk/filterHelper';
import { FacilityOrderPayPackagesFilterName } from './filterUtils';

const useStyles = makeStyles()({
  fillterCollapse: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
});

const PREFIX: Record<string, string> = {
  [FacilityOrderPayPackagesFilterName.orderId]: 'Order ID',
  [FacilityOrderPayPackagesFilterName.weeklyGrossMargin]: 'Weekly Gross Margin',
  [FacilityOrderPayPackagesFilterName.shift]: 'Shift',
  [FacilityOrderPayPackagesFilterName.postingStatus]: 'Posting Status',
};

export const FacilityOrderPayPackagesChips = (props: { onApplyFilter: (filters: any, newSelectedChipFilters?: any) => void }) => {
  const { classes } = useStyles();
  const [chips, setChips] = useState<Record<string, any>>({});
  const [showChipsCollapseBtn, setShowChipsCollapseBtn] = useState(false);
  const hiddenSubmitButton = createRef<HTMLButtonElement>();
  const { setValue, getValues } = useFormContext();
  const filterObject = useSelector(selectFilters);
  const [checked, setChecked] = React.useState(false);
  const divRef = useRef<HTMLDivElement>(null);

  const handleChange = () => setChecked(prev => !prev);

  const getNewSelection = (key: string, chip: any) => ({
    ...filterObject?.selectedChipFilters,
    [key]: determineChipSelected(
      chip,
      filterObject?.selectedChipFilters ? filterObject?.selectedChipFilters[key] : undefined,
    ),
  });

  const handleChipDelete = (key: string, chip: any) => {
    const fieldValues = getValues(key) ?? getValues(chip.value);
    const valueToRemove = chip?.value?.value ?? chip?.value;
    const newFieldValues = Array.isArray(fieldValues)
      ? fieldValues.filter((item: any) => (item?.value ?? item) !== valueToRemove)
      : null;

    setValue(key, newFieldValues, { shouldDirty: true });

    if (chip.isSelected) {
      const newSelections = getNewSelection(key, chip);
      props.onApplyFilter(
        {
          ...getValues(),
          [key]: Array.isArray(fieldValues) ? [...(newFieldValues || [])] : null,
        },
        newSelections,
      );
    } else {
      hiddenSubmitButton.current?.click();
    }
  };

  const handleChipClick = (key: string, chip: any) => {
    if (!Array.isArray(getValues(key))) return;
    const newSelections = getNewSelection(key, chip);
    const next = buildChips(getValues(), newSelections);
    setChips(next);
    props.onApplyFilter(getValues(), newSelections);
  };

  const buildChips = (filters: any, selectedChipFilters: any) => {
    return Object.fromEntries(
      Object.entries(filters || {}).map(([key, value]) => [
        key,
        generateFieldChips(
          value,
          selectedChipFilters ? selectedChipFilters[key] : undefined,
          PREFIX[key] ?? key,
          false,
          undefined,
          false,
        ),
      ]),
    );
  };

  const computedChips = useMemo(() => {
    return filterObject?.filters ? buildChips(filterObject.filters, filterObject.selectedChipFilters) : {};
  }, [filterObject?.filters, filterObject?.selectedChipFilters]);

  useEffect(() => {
    setChips(computedChips);
  }, [computedChips]);

  useEffect(() => {
    const hasAny = Object.keys(chips).some(key => {
      const value = chips[key];
      if (Array.isArray(value) && value.length > 0) return true;
      if (typeof value === 'object' && value !== null && Object.keys(value).length > 0) return true;
      return false;
    });
    setShowChipsCollapseBtn(hasAny);
  }, [chips]);

  return (
    <>
      {showChipsCollapseBtn ? (
        <Grid container spacing={2}>
          <Grid item xs={11}>
            <Collapse in={checked} collapsedSize={38}>
              <div ref={divRef}>
                <ChipsContainer
                  chips={chips}
                  onDelete={(field: string, chip: any) => handleChipDelete(field, chip)}
                  onClick={(field: string, chip: any) => handleChipClick(field, chip)}
                  isCollapse={!checked}
                />
              </div>
              <button ref={hiddenSubmitButton} style={{ display: 'none' }} type="submit" />
            </Collapse>
          </Grid>
          <Grid item xs={1} className={classes.fillterCollapse}>
            {(divRef?.current?.clientHeight ?? 0) > 38 && (
              <SearchPageIcons KeyboardArrowIcon onAdvancedChipsClick={() => handleChange()} checked={checked} />
            )}
          </Grid>
        </Grid>
      ) : (
        <>
          <ChipsContainer
            chips={chips}
            onDelete={(field: string, chip: any) => handleChipDelete(field, chip)}
            onClick={(field: string, chip: any) => handleChipClick(field, chip)}
          />
          <button ref={hiddenSubmitButton} style={{ display: 'none' }} type="submit" />
        </>
      )}
    </>
  );
};
